<script setup lang="ts">
import { useField } from 'vee-validate'
import type { FieldUIProp } from '~/types/elementsUi'
import type { VInputProps } from '~~/app/types/formProps'
import { useInjectedId } from '~/composables/useInjectedId'
import type { FAIconName } from '#shared/types/generated-icons'

type FormInputEmits = {
  labelIconClick: []
}

interface FormInputProps extends VInputProps {
  name: string
  label?: string
  hidden?: boolean // Visually hide field (i.e. to help browsers with password storing)
  labelIcon?: FAIconName
  iconLeft?: FAIconName
  iconRight?: FAIconName
  ui?: FieldUIProp
}

defineOptions({
  inheritAttrs: false
})

const emits = defineEmits<FormInputEmits>()
const props = withDefaults(defineProps<FormInputProps>(), {
  label: undefined,
  type: 'text',
  placeholder: '',
  autocomplete: undefined,
  inputMode: 'text',
  tabIndex: undefined,
  labelIcon: undefined,
  iconLeft: undefined,
  iconRight: undefined,
  ui: undefined
})

const { value, handleBlur, errors } = useField<string>(props.name)

const $form = useInjectedId(props.name)

const color = computed(() => {
  if (props.color) {
    return props.color
  } else if (errors.value.length > 0) {
    return 'danger'
  } else if (value.value?.length > 0) {
    return 'success'
  }
  return undefined
})

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, { field: props.hidden && 'hidden' })
)
</script>

<template>
  <VField
    :label
    :color
    :message="errors.join('\n')"
    :ui="computedUi"
    :label-icon
    :icon-left
    :icon-right
    :name
    @label-icon-click="emits('labelIconClick')"
  >
    <template v-if="$slots['label']" #label>
      <slot name="label" />
    </template>

    <template v-if="$slots['addons']" #addons>
      <slot name="addons" />
    </template>

    <slot />
    <VInput
      :id="$form.elementId.value"
      v-model="value"
      v-bind="$attrs"
      :type
      :placeholder
      :autocomplete
      :inputmode="inputMode"
      :tabindex
      :step
      :disabled
      :read-only
      :aria-hidden="hidden"
      @blur="handleBlur"
    />
  </VField>
</template>

<style scoped></style>
